/* eslint-disable no-undef */
import { Component } from 'react';
import PropTypes from 'prop-types';
import vars from 'src/common/vars';
import { hasPairAuthFlowParam } from 'src/common/utils/auth/pairAuthFlowHelpers';
import getRequiredLabel from '../../utils/form/getRequiredLabel';
import cssVariables from '../../styles/variables';
import { SMALL } from '../../styles/breakpoints';
import {
  SUBSCRIBE_PAYMENT_FORM_CARD_NUMBER,
  SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_MONTH,
  SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_YEAR,
  SUBSCRIBE_PAYMENT_FORM_CARD_CVV,
} from '../../constants/localizations/subscription';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import withRecurlyTracking from './withRecurlyTracking';

/**
 * A component that is responsible for configuring the global recurly library in componentDidMount
 * @param FormComponent {ReactComponent}
 * @returns {ReactComponent}
 */
export default function withRecurlyConfig(FormComponent) {
  class WithRecurlyConfig extends Component {
    static propTypes = {
      recurly: PropTypes.object.isRequired,
      breakpoint: PropTypes.number.isRequired,
    };

    static contextType = LocationAndLocalizationContext;

    /**
     * Calls recurly.configure every time the component mounted so that the library
     * re-injects the iframes (Hosted Fields) that it uses to captured credit card
     * billing information. Once all of the Hosted Fields have initialized correctly,
     * the 'ready' event is emitted from the library.
     */
    componentDidMount() {
      /**
       * A recurly configuration object that can be used to style recurly Hosted Fields and
       * add placeholders.
       *
       * Documentation links:
       *  1) Test card numbers: https://docs.recurly.com/docs/test#section-test-credit-card-numbers
       *  2) Recurly configure call: https://dev.recurly.com/docs/recurly-js-getting-started#section-configure
       *  3) Recurly token call: https://dev.recurly.com/docs/recurly-js-getting-a-token#section-reference
       *  5) Styling hosted fields: https://dev.recurly.com/docs/recurly-js-styling-card-fields
       **/
      this.props.recurly.configure(this.configuration());
    }

    /**
     * This lifecycle method is included to reconfigure the recurly card fields for different
     * screen sizes.
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
      // Screen size hasn't changed so we don't need to reconfigure recurly
      if (prevProps.breakpoint === this.props.breakpoint) {
        return;
      }

      this.props.recurly.configure(this.configuration());
    }

    /**
     * Method that returns the Recurly.js configuration object.
     * @returns {object}
     */
    configuration() {
      const { getLocalizedText, location } = this.context;

      const isPairingFlow = hasPairAuthFlowParam(location?.query);

      // need to have !important here so IE 11 respects style rule
      const individualFieldColor = isPairingFlow
        ? { color: `white !important` }
        : { color: `${cssVariables['--secondary-color-6']} !important` };
      const allFieldStyles = isPairingFlow
        ? {
            fontColor: 'white',
            fontFamily: cssVariables['--auxiliary-font-header'],
            padding: '0px',
          }
        : {
            fontColor: 'black',
            padding: '0 0 0 12px',
          };

      return {
        publicKey: vars.get('RECURLY_PUBLIC_KEY'),
        fields: {
          all: {
            style: {
              fontSize: '16px',
              ...allFieldStyles,
            },
          },
          card: {
            tabIndex: 3,
            displayIcon: this.props.breakpoint >= SMALL,
            style: {
              placeholder: {
                content: {
                  number: getRequiredLabel(
                    getLocalizedText(SUBSCRIBE_PAYMENT_FORM_CARD_NUMBER),
                  ),
                  expiry:
                    // eslint-disable-next-line max-len
                    getRequiredLabel(
                      `${getLocalizedText(
                        SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_MONTH,
                      )}/${getLocalizedText(
                        SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_YEAR,
                      )}`,
                    ),
                  cvv: getRequiredLabel(
                    getLocalizedText(SUBSCRIBE_PAYMENT_FORM_CARD_CVV),
                  ),
                },
                ...individualFieldColor,
              },
            },
          },
          number: {
            tabIndex: 3,
            style: {
              placeholder: {
                content: getRequiredLabel(
                  getLocalizedText(SUBSCRIBE_PAYMENT_FORM_CARD_NUMBER),
                ),
                ...individualFieldColor,
              },
            },
          },
          month: {
            tabIndex: 4,
            style: {
              placeholder: {
                content: getLocalizedText(
                  SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_MONTH,
                ),
                ...individualFieldColor,
              },
            },
          },
          year: {
            tabIndex: 5,
            style: {
              placeholder: {
                content: getRequiredLabel(
                  getLocalizedText(SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_YEAR),
                ),
                ...individualFieldColor,
              },
            },
          },
          cvv: {
            tabIndex: 6,
            style: {
              placeholder: {
                content: getRequiredLabel(
                  getLocalizedText(SUBSCRIBE_PAYMENT_FORM_CARD_CVV),
                ),
                ...individualFieldColor,
              },
            },
          },
        },
      };
    }

    render() {
      return <FormComponent {...this.props} />;
    }
  }

  return withRecurlyTracking(WithRecurlyConfig);
}
